import React, { useState, useEffect } from "react";
import buildGraphQLProvider from "ra-data-graphql-simple";
import { Admin, CheckForApplicationUpdate, Resource, DataProvider, LayoutProps, Layout } from "react-admin";

import buildLink from "./client/link";
import authProvider, { getCsrfToken, setCsrfToken } from "./authProvider";
import i18nProvider from "./i18n";
import { SYSTEM_ADMINISTRATOR } from "./authProvider/roles";
import customBuildQuery from "./dataProvider/customBuildQuery";

import {
  CustomerList,
  CustomerShow,
  CustomerCreate,
  CustomerEdit,
} from "./customers";
import { DomainList, DomainShow, DomainCreate, DomainEdit } from "./domains";
import {
  DNSZoneShow,
  DNSZoneEdit,
} from "./dnsZone";
import {
  MailAccountList,
  MailAccountCreate,
  MailAccountEdit,
} from "./mailAccounts";
import {
  MailForwardList,
  MailForwardCreate,
  MailForwardEdit,
} from "./mailForwards";
import {
  ServiceTemplateList,
  ServiceTemplateCreate,
  ServiceTemplateEdit,
} from "./serviceTemplates";
import {
  ServiceBookingList,
  ServiceBookingCreate,
  ServiceBookingEdit,
} from "./serviceBookings";
import {
  InvoiceExportList,
  InvoiceExportShow,
} from "./invoiceExports";
import {
  AccountList,
  AccountShow,
  AccountCreate,
  AccountEdit,
} from "./accounts";
import LoginPage from "./LoginPage";

const link = buildLink({ getCsrfToken, setCsrfToken });

type DepsState = {
  dataProvider?: DataProvider;
};

const App = (): JSX.Element => {
  // Put async deps in object since useState reduces functions by default (Wat?)
  const [deps, setDeps] = useState<DepsState>({});

  useEffect(() => {
    buildGraphQLProvider({
      clientOptions: {
        link,
      },
      buildQuery: customBuildQuery,
    })
      .then((dataProvider) => setDeps((deps) => ({ ...deps, dataProvider })))
      .catch((error) => {
        // TODO Notify about error when building the data provider
        console.error(error);
      });
  }, []);

  if (!deps.dataProvider) {
    return <div>Loading</div>;
  }

  return (
    <Admin
      dataProvider={deps.dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      loginPage={LoginPage}
      disableTelemetry
      layout={CustomLayout}
    >
      {(permissions) => [
        <Resource
          name="Customer"
          list={CustomerList}
          show={CustomerShow}
          create={
            permissions === SYSTEM_ADMINISTRATOR ? CustomerCreate : undefined
          }
          edit={permissions === SYSTEM_ADMINISTRATOR ? CustomerEdit : undefined}
        />,
        <Resource
          name="Domain"
          list={DomainList}
          show={DomainShow}
          create={
            permissions === SYSTEM_ADMINISTRATOR ? DomainCreate : undefined
          }
          edit={permissions === SYSTEM_ADMINISTRATOR ? DomainEdit : undefined}
        />,
        <Resource
          name="MailAccount"
          list={MailAccountList}
          create={MailAccountCreate}
          edit={MailAccountEdit}
        />,
        <Resource
          name="MailForward"
          list={MailForwardList}
          create={MailForwardCreate}
          edit={MailForwardEdit}
        />,
        ...(permissions === SYSTEM_ADMINISTRATOR
          ? [
              <Resource
                name="ServiceTemplate"
                list={ServiceTemplateList}
                // show={ServiceTemplateShow}
                create={ServiceTemplateCreate}
                edit={ServiceTemplateEdit}
              />,
              <Resource
                name="ServiceBooking"
                list={ServiceBookingList}
                // show={ServiceTemplateShow}
                create={ServiceBookingCreate}
                edit={ServiceBookingEdit}
              />,
              <Resource
                name="InvoiceExport"
                list={InvoiceExportList}
                show={InvoiceExportShow}
              />,
              <Resource
                name="Account"
                list={AccountList}
                show={AccountShow}
                create={AccountCreate}
                edit={AccountEdit}
              />,
              <Resource
                name="DNSZone"
                show={DNSZoneShow}
                edit={DNSZoneEdit}
              />,
            ]
          : []),
      ]}
    </Admin>
  );
};

const CustomLayout = ({children, ...props}: LayoutProps) => {
  return <Layout {...props}>
  {children}
  <CheckForApplicationUpdate interval={300} />
</Layout>
};

export default App;
